'use client';
import * as React from 'react';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { Icon } from '@bayada/shared/ui-components';
import NavigationLink, {
  NavigationLinkProps
} from '../navigation-link/navigation-link';
import { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import debounce from 'lodash/debounce';
import { MarkDown } from '@bayada/shared/ui-components';
import { useAppContext } from 'apps/bayada/context/app-context';

interface menuListType {
  triggerClassName?: string;
  textClassName?: string;
  buttonIcon?: React.ReactNode;
  iconStart?: boolean;
  item?: NavigationLinkProps;
  drawerItem?: boolean;
}

interface NestedMenuItemProps {
  item: NavigationLinkProps;
  onClose: (
    event: Event | React.SyntheticEvent,
    value?: NavigationLinkProps
  ) => void;
}

/**
 * NestedMenuItem component for handling multi-level dropdown navigation
 */
const NestedMenuItem: React.FC<NestedMenuItemProps> = ({ item, onClose }) => {
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const hasSubMenu = Boolean(item.dropdown?.length);

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (hasSubMenu) {
        setAnchorEl(event.currentTarget);
        setSubMenuOpen(true);
      }
    },
    [hasSubMenu]
  );

  const handleMouseLeave = useCallback(() => {
    setSubMenuOpen(false);
    setAnchorEl(null);
  }, []);

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      if (hasSubMenu) {
        event.preventDefault();
        return;
      }
      onClose(event, item);
    },
    [hasSubMenu, onClose, item]
  );

  const handleSubMenuClose = useCallback(
    (event: Event | React.SyntheticEvent, value?: NavigationLinkProps) => {
      setSubMenuOpen(false);
      setAnchorEl(null);
      if (value) {
        onClose(event, value);
      }
    },
    [onClose]
  );

  return (
    <>
      <MenuItem
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`app-menu-list-items`}
      >
        {hasSubMenu ? (
          item?.text && (
            <>
              <span
                dangerouslySetInnerHTML={{ __html: MarkDown(item.text) }}
                className={`${hasSubMenu ? 't-14-15 font-inter leading-6' : 't-13 font-frutiger set-frutiger font-bold normal-case leading-[1]'}`}
              />

              <Icon
                iconName="chevronRight"
                className="svg-icon icon-24 flex items-center justify-center ml-auto"
              />
            </>
          )
        ) : (
          <NavigationLink
            {...item}
            isDrawerDropdownLink={true}
            className="t-16-17 font-inter leading-none no-underline w-full text-left inline-flex"
            textClass="t-14-15 font-inter leading-6"
          />
        )}
      </MenuItem>

      {hasSubMenu && (
        <Popper
          open={subMenuOpen}
          anchorEl={anchorEl}
          placement="right-start"
          transition
          disablePortal={false}
          style={{ zIndex: 1400 }}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 4]
              }
            }
          ]}
        >
          {({ TransitionProps }) => (
            <Grow
              {...TransitionProps}
              timeout={150}
              style={{
                transformOrigin: 'left top'
              }}
            >
              <Paper
                onMouseEnter={() => setSubMenuOpen(true)}
                onMouseLeave={handleMouseLeave}
                elevation={8}
                style={{ marginLeft: '4px' }}
              >
                <MenuList className="app-menu-list">
                  {item.dropdown?.map(
                    (subItem: NavigationLinkProps, index: number) => (
                      <NestedMenuItem
                        key={subItem.text || `sub-${index}`}
                        item={subItem}
                        onClose={handleSubMenuClose}
                      />
                    )
                  )}
                </MenuList>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </>
  );
};

/**
 * The `MenuListComposition` function in TypeScript React is a component that renders a button with a
 * dropdown menu using Material-UI components.
 * @param event - The `event` parameter in the `handleClose` function and `handleCloseOnScrollOutside`
 * function represents the event that triggered the function. It can be either a standard JavaScript
 * `Event` or a React `SyntheticEvent`. This parameter is used to check if the target element of the
 * event
 */
const MenuListComposition: React.FC<menuListType> = ({
  triggerClassName,
  buttonIcon,
  iconStart,
  item
}) => {
  const route = useRouter();
  const [open, setOpen] = useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const prevOpen = React.useRef(open);
  const { context } = useAppContext();
  const { userAgent } = context;

  const handleToggle = useCallback(
    debounce(() => {
      setOpen((prevOpen) => !prevOpen);
    }, 300),
    []
  );

  const handleClose = useCallback(
    (event: Event | React.SyntheticEvent, value?: NavigationLinkProps) => {
      if (
        anchorRef.current &&
        anchorRef.current.contains(event.target as HTMLElement)
      ) {
        return;
      }
      setOpen(false);
      if (value?.url && window) {
        const _target = value?.target === 'newtab' ? '_blank' : '_self';
        if (userAgent === 'Firefox' || _target === '_blank') {
          window.open(value?.url, _target);
        } else {
          route.push(value?.url);
        }
      }
    },
    [userAgent, route]
  );

  const handleListKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }
    prevOpen.current = open;
  }, [open]);

  useEffect(() => {
    const handleCloseOnScrollOutside = (event: Event) => {
      if (
        anchorRef?.current &&
        !anchorRef?.current?.contains(event?.target as Node)
      ) {
        setOpen(false);
      }
    };

    window.addEventListener('scroll', handleCloseOnScrollOutside);
    return () => {
      window.removeEventListener('scroll', handleCloseOnScrollOutside);
    };
  }, []);

  const dropdownItems = React.useMemo(
    () =>
      item?.dropdown?.map((value: NavigationLinkProps, index: number) => (
        <NestedMenuItem
          key={value.text || `item-${index}`}
          item={value}
          onClose={handleClose}
        />
      )) || [],
    [item?.dropdown, handleClose]
  );

  return (
    <div className="flex gap-3">
      <Button
        variant="text"
        color="secondary"
        disableFocusRipple
        ref={anchorRef}
        aria-controls={open ? 'composition-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        className={`menu-dropdown ${triggerClassName || ''}`}
        startIcon={iconStart ? buttonIcon : null}
        endIcon={
          <Icon
            iconName="chevronDownThin"
            className="svg-icon icon-12 flex items-center justify-center"
          />
        }
        aria-label="primary-button"
      >
        {item?.text && (
          <span
            dangerouslySetInnerHTML={{ __html: MarkDown(item.text) }}
            className="t-13 font-frutiger set-frutiger font-bold normal-case leading-[1]"
          />
        )}
      </Button>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            timeout={0}
            style={{
              transformOrigin:
                placement === 'bottom-start' ? 'left top' : 'left bottom'
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="composition-menu"
                  className="app-menu-list"
                  aria-labelledby="composition-button"
                  onKeyDown={handleListKeyDown}
                  data-lenis-prevent
                >
                  {dropdownItems}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

export default MenuListComposition;
